import { Link } from "react-router-dom";
import { Leaf, Sparkles, ShieldCheck, CalendarHeart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const HERO_ILLUSTRATION = "/branding/hero-botanical.svg";
const SAFETY_ILLUSTRATION = "/branding/safety-apothecary.svg";

export default function Landing() {
  const { user } = useAuth();
  const cta = user ? "/questionnaire" : "/auth?mode=register";

  return (
    <main className="relative overflow-hidden">
      {/* HERO */}
      <section className="relative max-w-6xl mx-auto px-6 sm:px-8 pt-16 sm:pt-24 pb-20">
        <div className="relative grid lg:grid-cols-12 gap-10 items-center fade-up">
          <div className="lg:col-span-7">
            <p className="label-eyebrow mb-6" data-testid="hero-eyebrow">
              Personalised · Natural · Evidence-Aware
            </p>
            <h1
              className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
              style={{ textWrap: "balance" }}
            >
              Find the natural supplements{" "}
              <span style={{ color: "var(--brand)" }}>
                your body has been <span className="whitespace-nowrap">asking&nbsp;for.</span>
              </span>
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed max-w-xl" style={{ color: "var(--text-2)" }}>
              Answer a short, thoughtful questionnaire. We pair a curated supplement library with
              a personalised explanation written for your unique concerns &mdash; sleep, stress,
              focus, energy, and more.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to={cta} className="btn-primary" data-testid="hero-cta">
                Start your assessment
              </Link>
              <Link to="/auth" className="btn-ghost text-sm" data-testid="hero-login">
                I already have an account
              </Link>
            </div>
            <p className="mt-6 text-xs" style={{ color: "var(--text-2)" }}>
              Educational only. Always consult a healthcare professional before starting new supplements.
            </p>
          </div>

          <div className="lg:col-span-5 relative">
            <div
              className="relative rounded-3xl overflow-hidden"
              style={{ border: "1px solid var(--border)", background: "var(--bg)" }}
            >
              <img
                src={HERO_ILLUSTRATION}
                alt="A composed botanical illustration of sage sprigs"
                className="w-full h-[420px] object-cover"
              />
            </div>
            <div
              className="absolute -bottom-10 -left-10 w-60 p-5 card-soft hidden sm:block shadow-sm"
              style={{ background: "var(--bg)", boxShadow: "0 12px 30px rgba(42,51,43,0.08)" }}
              data-testid="hero-card-feature"
            >
              <Sparkles className="w-5 h-5 mb-2" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
              <p className="font-serif text-xl leading-snug" style={{ color: "var(--text)" }}>
                AI-personalised, never generic.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 py-20" style={{ background: "var(--bg-2)" }}>
        <div className="rounded-3xl p-8 sm:p-12" style={{ background: "var(--bg-2)" }}>
          <div className="grid sm:grid-cols-3 gap-8">
            {[
              { icon: Leaf, title: "1. Tell us your concerns", body: "Quick questionnaire on energy, sleep, stress, focus, and lifestyle." },
              { icon: Sparkles, title: "2. Receive a tailored plan", body: "Curated supplements with AI-written guidance specific to you." },
              { icon: CalendarHeart, title: "3. Track and refine", body: "Log what you take daily. Watch streaks. Refine over weeks." },
            ].map((s, i) => (
              <div key={i} className="fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                <s.icon className="w-6 h-6 mb-4" strokeWidth={1.5} style={{ color: "var(--brand)" }} />
                <h3 className="font-serif text-2xl mb-2">{s.title}</h3>
                <p style={{ color: "var(--text-2)" }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="rounded-3xl overflow-hidden" style={{ border: "1px solid var(--border)", background: "var(--bg)" }}>
          <img src={SAFETY_ILLUSTRATION} alt="Apothecary jar with sage sprig and mortar with pestle" className="w-full h-[420px] object-cover" />
        </div>
        <div>
          <p className="label-eyebrow mb-4">Safe by design</p>
          <h2 className="font-serif text-3xl sm:text-4xl mb-5 leading-tight">
            Every recommendation comes with dosage, timing, warnings & interactions.
          </h2>
          <p style={{ color: "var(--text-2)" }} className="leading-relaxed">
            We surface the same details a careful practitioner would &mdash; how much, when to take it,
            who should avoid it, and what it doesn&apos;t mix well with. Export your plan as a PDF
            to bring to your next appointment.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <ShieldCheck className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--brand)" }} />
            <span className="text-sm" style={{ color: "var(--text-2)" }}>
              Educational, not medical advice. Built for thoughtful self-care.
            </span>
          </div>
          <div className="mt-10">
            <Link to={cta} className="btn-primary" data-testid="cta-secondary">
              Build my plan
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-8 text-sm flex flex-wrap items-center justify-between gap-3" style={{ color: "var(--text-2)" }}>
          <span>© {new Date().getFullYear()} Alternavit</span>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:underline" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:underline" data-testid="footer-terms">Terms</Link>
            <span className="hidden sm:inline">Educational, not medical advice.</span>
          </div>
        </div>
      </footer>
    </main>
  );
}
